import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ShieldCheck, ClipboardText, ChartLine, FileText, Camera, Handshake, ArrowRight, CheckCircle } from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import QuickLinksSection from "@/components/QuickLinksSection";

const FEATURE_ICONS = [Camera, ShieldCheck, ClipboardText, FileText, ChartLine, Handshake];

const FALLBACK = {
    branding: {
        brand_name: "PRC CMC",
        brand_sub: "FLEET CONTROL OS",
        brand_tagline: "点検を標準化し、安全を仕組みに変える。",
        hero_image_url: "https://customer-assets.emergentagent.com/job_drive-compliance/artifacts/0olrabkq_PRC-CMC%20haikei.png",
        logo_data_url: null,
    },
    landing: {
        hero_title_1: "点検を標準化し、",
        hero_title_2: "安全を仕組みに",
        hero_title_3: "変える。",
        hero_sub: "複数車両を保有する法人向けクラウド型 車両管理・点検・運行支援 SaaS。",
        cta_primary: "無料ではじめる",
        cta_secondary: "ログイン",
        stats: [
            { value: "99.9%", label: "SLA 目標稼働率" },
            { value: "24h", label: "使用前点検強制" },
            { value: "3 Roles", label: "権限管理" },
            { value: "PDF", label: "法令対応帳票" },
        ],
        features: [],
        footer_note: "点検を標準化し、安全を仕組みに変える。",
    },
    pricing_display: { starter_price: 9800, business_price: 49800, enterprise_price: 198000 },
    operator: { legal_name: "株式会社PRC", website: "https://prc-cmc.site" },
};

export default function Landing() {
    const { user } = useAuth();
    const [cms, setCms] = useState(FALLBACK);
    const [loaded, setLoaded] = useState(false);

    useEffect(() => {
        api.get("/settings/public")
            .then((r) => setCms({ ...FALLBACK, ...r.data, branding: { ...FALLBACK.branding, ...r.data.branding }, landing: { ...FALLBACK.landing, ...r.data.landing } }))
            .catch(() => {})
            .finally(() => setLoaded(true));
    }, []);

    const { branding, landing, pricing_display, operator } = cms;
    const pricingPlans = [
        { key: "starter", name: "Starter", price: (pricing_display.starter_price || 9800).toLocaleString(), unit: "月額", desc: "小規模拠点・最大50台", bullet: ["車両台帳", "使用前点検", "帳票PDF", "通知（社内メール）"] },
        { key: "business", name: "Business", price: (pricing_display.business_price || 49800).toLocaleString(), unit: "月額", desc: "中規模法人・最大300台", bullet: ["Starter の全機能", "OCR 車検証登録", "AI 写真異常検知", "API 連携"] },
        { key: "enterprise", name: "Enterprise", price: (pricing_display.enterprise_price || 198000).toLocaleString(), unit: "月額", desc: "無制限・SLA 保証", bullet: ["Business の全機能", "SLA 99.9%", "専用サポート", "カスタム対応"] },
    ];

    return (
        <div className="min-h-screen bg-[var(--prc-bg)] text-[var(--prc-ink)]" data-testid="landing-page">
            {/* Header */}
            <header className="border-b border-[var(--prc-border)] sticky top-0 bg-[var(--prc-surface)]/95 backdrop-blur z-20">
                <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
                    <Link to="/" className="flex items-center gap-3" style={{ visibility: loaded ? "visible" : "hidden" }}>
                        {branding.logo_data_url ? (
                            <img src={branding.logo_data_url} alt="logo" className="h-8" />
                        ) : (
                            <span className="w-2 h-2 rounded-full bg-[var(--prc-green)]" style={{ boxShadow: "0 0 8px var(--prc-green)" }} />
                        )}
                        <div>
                            <p className="font-mono text-[14px] font-extrabold tracking-[0.1em] text-[var(--prc-ink-2)]" data-testid="brand-name">{branding.brand_name}</p>
                            <p className="prc-label-overline mt-0.5">{branding.brand_sub}</p>
                        </div>
                    </Link>
                    <div className="flex items-center gap-3">
                        {user ? (
                            <Link to="/dashboard" className="prc-btn-primary" data-testid="go-dashboard-btn">ダッシュボードへ</Link>
                        ) : (
                            <>
                                <Link to="/login" className="text-sm font-semibold hover:underline" data-testid="nav-login-link">{landing.cta_secondary || "ログイン"}</Link>
                                <Link to="/register" className="prc-btn-primary" data-testid="nav-register-link">{landing.cta_primary || "無料ではじめる"}</Link>
                            </>
                        )}
                    </div>
                </div>
            </header>

            {/* Hero */}
            <section className="relative">
                <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${branding.hero_image_url})` }} />
                <div className="absolute inset-0 bg-black/60" />
                <div className="relative max-w-7xl mx-auto px-6 py-24 lg:py-36 text-white">
                    <p className="prc-label-overline !text-[var(--prc-green)] mb-5">// MISSION · {branding.brand_name}</p>
                    <h1 className="text-[1.575rem] sm:text-[2rem] lg:text-[2.7rem] font-bold tracking-tight leading-[1.05] max-w-4xl" data-testid="hero-title">
                        {landing.hero_title_1}<br />
                        <span className="text-[var(--prc-green)]">{landing.hero_title_2}</span>{landing.hero_title_3}
                    </h1>
                    <p className="mt-6 text-lg text-white/80 max-w-2xl leading-relaxed">{landing.hero_sub}</p>
                    <div className="mt-10 flex flex-wrap gap-4">
                        <Link to="/register" className="prc-btn-accent inline-flex items-center gap-2" data-testid="hero-register-btn">
                            {landing.cta_primary} <ArrowRight size={16} />
                        </Link>
                        <Link to="/login" className="prc-btn-ghost !text-white !border-white/40 hover:!bg-white/10" data-testid="hero-login-btn">
                            {landing.cta_secondary}
                        </Link>
                    </div>
                    <div className="mt-14 grid grid-cols-2 md:grid-cols-4 gap-8 max-w-3xl" data-testid="hero-stats">
                        {(landing.stats || []).map((s, i) => (
                            <div key={i}>
                                <p className="text-3xl font-mono font-bold tracking-tight">{s.value}</p>
                                <p className="text-xs text-white/60 uppercase tracking-wider mt-1">{s.label}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Features */}
            <section className="max-w-7xl mx-auto px-6 py-24">
                <p className="prc-label-overline mb-3">主要機能</p>
                <h2 className="text-3xl lg:text-4xl font-bold tracking-tight max-w-3xl">現場の確認を、経営資産に変える</h2>
                <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-3 gap-px bg-[var(--prc-border)] border border-[var(--prc-border)]" data-testid="features-grid">
                    {(landing.features || []).map((f, i) => {
                        const Icon = FEATURE_ICONS[i % FEATURE_ICONS.length];
                        return (
                            <div key={i} className="bg-[var(--prc-surface)] p-6 hover:bg-[var(--prc-surface-2)] transition-colors">
                                <Icon size={24} weight="regular" className="text-[var(--prc-green)]" />
                                <h3 className="mt-4 font-semibold text-base text-[var(--prc-ink-2)]">{f.title}</h3>
                                <p className="mt-2 text-sm text-[var(--prc-muted)] leading-relaxed">{f.desc}</p>
                            </div>
                        );
                    })}
                </div>
            </section>

            {/* Pricing */}
            <section className="bg-[var(--prc-surface)] border-t border-[var(--prc-border)]">
                <div className="max-w-7xl mx-auto px-6 py-24">
                    <p className="prc-label-overline mb-3">料金プラン</p>
                    <h2 className="text-3xl lg:text-4xl font-bold tracking-tight text-[var(--prc-ink-2)]">規模に合わせて選択</h2>
                    <div className="mt-12 grid md:grid-cols-3 gap-6" data-testid="pricing-grid">
                        {pricingPlans.map((p, i) => (
                            <div key={p.key} className={`prc-card p-8 relative ${i === 1 ? "ring-2 ring-[var(--prc-green)]" : ""}`}>
                                {i === 1 && <span className="absolute -top-3 left-6 prc-badge ok">人気</span>}
                                <p className="prc-label-overline">{p.name}</p>
                                <p className="mt-3 text-4xl font-mono font-bold tracking-tight text-[var(--prc-ink-2)]">
                                    ¥{p.price}<span className="text-sm font-normal text-[var(--prc-muted)] ml-2 font-sans">/ {p.unit}</span>
                                </p>
                                <p className="mt-2 text-sm text-[var(--prc-muted)]">{p.desc}</p>
                                <ul className="mt-6 space-y-2">
                                    {p.bullet.map((b) => (
                                        <li key={b} className="text-sm flex items-start gap-2">
                                            <CheckCircle size={16} className="mt-0.5 text-[var(--prc-green)]" />
                                            <span>{b}</span>
                                        </li>
                                    ))}
                                </ul>
                                <Link to="/register" className={`mt-8 inline-flex w-full justify-center ${i === 1 ? "prc-btn-primary" : "prc-btn-ghost"}`} data-testid={`plan-cta-${p.key}`}>
                                    {i === 2 ? "お問い合わせ" : "はじめる"}
                                </Link>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            <QuickLinksSection />

            <footer className="border-t border-[var(--prc-border)] bg-[var(--prc-bg)]">
                <div className="max-w-7xl mx-auto px-6 py-10 flex flex-col sm:flex-row justify-between text-sm text-[var(--prc-muted)]">
                    <div>
                        <p className="font-mono font-extrabold text-[var(--prc-ink-2)] tracking-[0.08em]">{branding.brand_name} · {branding.brand_sub}</p>
                        <p className="mt-1">{landing.footer_note}</p>
                    </div>
                    <div className="mt-4 sm:mt-0 text-xs text-right">
                        <p>© {new Date().getFullYear()} {operator.legal_name || branding.brand_name}. All rights reserved.</p>
                        {operator.website && <a href={operator.website} className="hover:text-[var(--prc-green)] block mt-1">{operator.website}</a>}
                    </div>
                </div>
            </footer>
        </div>
    );
}
